import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../routes';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PageHeader from '../common/PageHeader';
import {
  useGetCollectionsQuery,
  useGetDashboardQuery,
  useGetPeriodTotalsQuery,
  useGetTopProductsQuery,
} from '../../api/reports/ReportApi';
import { CollectionRow, PeriodTotals } from '../../types/reports';
import { longDate } from '../../utils/dates';

type Period = 'TODAY' | 'WEEK' | 'MONTH' | 'YEAR' | 'OVERALL' | 'CUSTOM';

const periodLabel: Record<Period, string> = {
  TODAY: 'Today',
  WEEK: 'This week',
  MONTH: 'This month',
  YEAR: 'This year',
  OVERALL: 'Overall',
  CUSTOM: 'Pick dates',
};

/** Local date as yyyy-MM-dd. toISOString() would shift to UTC and lose a day. */
const iso = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const todayIso = () => iso(new Date());

const monthStart = () => {
  const now = new Date();
  return iso(new Date(now.getFullYear(), now.getMonth(), 1));
};

const yearStart = () => iso(new Date(new Date().getFullYear(), 0, 1));

/** Monday of the current week - what a shop means by "this week", not the last 7 days. */
const weekStart = () => {
  const now = new Date();
  const mondayOffset = (now.getDay() + 6) % 7;
  return iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - mondayOffset));
};

const EMPTY_TOTALS: PeriodTotals = { bills: 0, sales: 0, profit: 0, taxable: 0, cgst: 0, sgst: 0, averageBill: 0 };

/**
 * One colour per bar in the seven-day chart. Seven flat blues told the owner nothing
 * the heights did not already say; distinct colours make it possible to point at
 * "the green one" across the counter.
 */
const TREND_COLOURS = ['#196ae5', '#00897b', '#7b1fa2', '#ef6c00', '#2e7d32', '#c2185b', '#0288d1'];

/** 2026-09-20 -> "20 Sep". The month matters at the turn of one. */
const barLabel = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  return `${value.slice(8)} ${date.toLocaleDateString('en-IN', { month: 'short' })}`;
};

const money = (value?: number | null) =>
  Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const paymentLabel: Record<string, string> = {
  CASH: 'Cash',
  UPI_CARD: 'UPI / Card',
  CREDIT: 'Credit',
  EMI: 'EMI',
  SPLIT: 'Split',
};

const paymentColour: Record<string, string> = {
  CASH: '#2e7d32',
  UPI_CARD: '#196ae5',
  CREDIT: '#ed6c02',
  EMI: '#7b1fa2',
};

/** One headline number. The label is what the owner would call it out loud. */
const Kpi = ({ label, value, hint, colour }: { label: string; value: string; hint?: string; colour?: string }) => (
  <Card sx={{ p: 2, height: '100%' }}>
    <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: '.04em' }}>
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700} sx={{ color: colour ?? 'text.primary', mt: 0.5 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
  </Card>
);

/**
 * The owner's first screen.
 *
 * <p>Built around one distinction the rest of the app cannot make for them: what
 * was <em>billed</em> today is not what was <em>collected</em> today. A credit sale
 * and an EMI sale are both revenue and neither is money in the drawer, so both
 * figures are on screen, side by side, and the cash line says what should be in the
 * till at closing.
 *
 * <p>The bars and the trend are drawn with plain boxes rather than a charting
 * library - seven days of sales does not justify 300 KB of JavaScript at the
 * counter.
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('TODAY');
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const { data, isLoading } = useGetDashboardQuery();

  // Which dates the panels below cover. Overall is the one period with no dates at
  // all - the backend reads that as "since the shop opened".
  const range: { fromDate?: string; toDate?: string } =
    period === 'TODAY'
      ? { fromDate: todayIso(), toDate: todayIso() }
      : period === 'WEEK'
        ? { fromDate: weekStart(), toDate: todayIso() }
        : period === 'MONTH'
          ? { fromDate: monthStart(), toDate: todayIso() }
          : period === 'YEAR'
            ? { fromDate: yearStart(), toDate: todayIso() }
            : period === 'CUSTOM'
              ? { fromDate, toDate }
              : {};

  const { data: periodCollections } = useGetCollectionsQuery(range);

  // Today, this month and overall come free with /dashboard. The rest need asking
  // for, so only those three avoid a second request.
  const bakedIn = period === 'TODAY' || period === 'MONTH' || period === 'OVERALL';
  const { data: rangeTotals, isFetching: fetchingTotals } = useGetPeriodTotalsQuery(range, { skip: bakedIn });

  // Top sellers follow the period for every choice, not only the ones that need a
  // request anyway - a panel that answers a different question from the selector
  // above it is how an owner ends up acting on the wrong list.
  const { data: rangeTop } = useGetTopProductsQuery(range);

  if (isLoading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { today, month, lastMonth, overall, trend, attention, topProducts, dueToUs, weOwe } = data;

  // One set of figures, whichever period is showing.
  const totals =
    period === 'TODAY' ? today : period === 'MONTH' ? month : period === 'OVERALL' ? overall : (rangeTotals ?? EMPTY_TOTALS);
  const collections = periodCollections ?? data.collectionsToday;
  const bestSellers = rangeTop ?? topProducts;

  /**
   * Zero and "not answered yet" are different things.
   *
   * <p>A picked range whose figures have not arrived used to draw ₹0, which reads as
   * "no sales that week" - a wrong answer rather than no answer. A dash says nothing
   * instead of saying something false.
   */
  const waitingForTotals = !bakedIn && !rangeTotals;
  const figure = (value?: number | null) => (waitingForTotals ? '—' : `₹ ${money(value)}`);

  // What the cards say underneath themselves: the dates behind the number.
  const rangeHint =
    period === 'OVERALL'
      ? 'Since the shop opened'
      : range.fromDate === range.toDate
        ? longDate(range.fromDate)
        : `${longDate(range.fromDate)} to ${longDate(range.toDate)}`;

  const growth =
    Number(lastMonth.sales) > 0
      ? ((Number(month.sales) - Number(lastMonth.sales)) / Number(lastMonth.sales)) * 100
      : null;

  const collectionMax = Math.max(...collections.rows.map((row: CollectionRow) => Number(row.billedAmount) || 0), 1);
  const trendMax = Math.max(...trend.map((point) => Number(point.sales) || 0), 1);

  /*
   * Each count says where to go and see it. On its own "3 product(s) out of stock"
   * tells the owner there is a problem and nothing about which three, so the next
   * thing they do is go looking for the report by hand.
   */
  const alerts = [
    { count: attention.outOfStock, text: 'product(s) out of stock', severity: 'error' as const, report: 'LOW_STOCK' },
    { count: attention.belowReorder, text: 'product(s) running low', severity: 'warning' as const, report: 'LOW_STOCK' },
    { count: attention.deadStock, text: 'batch(es) unsold over 90 days', severity: 'info' as const, report: 'DEAD_STOCK' },
    { count: attention.cancelledToday, text: 'bill(s) cancelled today', severity: 'warning' as const, report: '' },
  ].filter((alert) => Number(alert.count) > 0);

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle={new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
      >
        <ToggleButtonGroup exclusive size="small" value={period} onChange={(_event, value) => value && setPeriod(value)}>
          {(Object.keys(periodLabel) as Period[]).map((key) => (
            <ToggleButton key={key} value={key} sx={{ textTransform: 'none' }}>
              {periodLabel[key]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Shown only for Pick dates, so the five common answers stay one click away
            and the owner is not asked to fill in two boxes to see today. */}
        {period === 'CUSTOM' && (
          <>
            <TextField
              label="From"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </>
        )}
      </PageHeader>

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <Kpi
            label="SALES"
            value={figure(totals.sales)}
            hint={waitingForTotals && fetchingTotals ? 'Reading...' : `${totals.bills} bill(s) · ${rangeHint}`}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi
            label="CASH IN HAND"
            value={`₹ ${money(collections.cashInHand)}`}
            hint={
              Number(collections.cashPaidOut) > 0
                ? `Cash in, less ${money(collections.cashPaidOut)} paid out`
                : 'Cash sales + cash collected'
            }
            colour="#2e7d32"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi label="PROFIT" value={figure(totals.profit)} hint="After what the stock cost" colour="#196ae5" />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi label="AVERAGE BILL" value={figure(totals.averageBill)} hint="Per bill in this period" />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        {/* ------------------------------------------- collections today */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.main">
              COLLECTIONS &middot; {periodLabel[period].toUpperCase()}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              What was billed, and how much of it actually arrived &middot; {rangeHint}
            </Typography>
            <Divider sx={{ my: 1.5 }} />

            {collections.rows.map((row: CollectionRow) => (
              <Box key={row.paymentType} sx={{ mb: 1.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body2">
                    {paymentLabel[row.paymentType]}
                    {row.billCount > 0 && (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}
                        ({row.billCount})
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹ {money(row.billedAmount)}
                  </Typography>
                </Stack>
                <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', mt: 0.5, overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      width: `${(Number(row.billedAmount) / collectionMax) * 100}%`,
                      bgcolor: paymentColour[row.paymentType],
                      transition: 'width .4s ease',
                    }}
                  />
                </Box>
                {Number(row.pendingAmount) > 0 && (
                  <Typography variant="caption" color="warning.main">
                    ₹ {money(row.pendingAmount)} still to come
                  </Typography>
                )}
              </Box>
            ))}

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Billed</Typography>
              <Typography variant="body2" fontWeight={600}>
                ₹ {money(collections.billedTotal)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Received on old bills</Typography>
              <Typography variant="body2">₹ {money(collections.receivedAgainstOldBills)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Actually collected
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                ₹ {money(collections.totalCollected)}
              </Typography>
            </Stack>
          </Card>
        </Grid>

        {/* ------------------------------------------- trend and month */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.main">
              LAST 7 DAYS
            </Typography>
            <Divider sx={{ my: 1.5 }} />

            <Stack direction="row" alignItems="flex-end" gap={1} sx={{ height: 120 }}>
              {trend.map((point, index) => (
                <Box key={point.date} sx={{ flex: 1, textAlign: 'center' }}>
                  <Box
                    title={`₹ ${money(point.sales)} · ${point.bills} bill(s)`}
                    sx={{
                      // A third of the column, centred: a slim bar reads as a
                      // measurement, a full-width block reads as a wall.
                      width: '33.33%',
                      mx: 'auto',
                      height: `${Math.max((Number(point.sales) / trendMax) * 100, 2)}px`,
                      bgcolor: TREND_COLOURS[index % TREND_COLOURS.length],
                      borderRadius: '4px 4px 0 0',
                      opacity: Number(point.sales) > 0 ? 1 : 0.25,
                      transition: 'height .4s ease',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                    {barLabel(point.date)}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  THIS MONTH
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  ₹ {money(month.sales)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {month.bills} bill(s) · profit ₹ {money(month.profit)}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="caption" color="text.secondary">
                  LAST MONTH
                </Typography>
                <Typography variant="h6">₹ {money(lastMonth.sales)}</Typography>
                {growth !== null && (
                  <Chip
                    size="small"
                    icon={growth >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    color={growth >= 0 ? 'success' : 'error'}
                    variant="outlined"
                    label={`${growth >= 0 ? '+' : ''}${growth.toFixed(0)}%`}
                  />
                )}
              </Box>
            </Stack>
          </Card>
        </Grid>

        {/* -------------------------------------------------- attention */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.main">
              NEEDS ATTENTION
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {alerts.length === 0 ? (
              <Alert severity="success" sx={{ py: 0 }}>
                Nothing out of stock, nothing sitting too long, no bills cancelled today.
              </Alert>
            ) : (
              <Stack gap={1}>
                {/* A real button in the alert's action slot, not a click handler on the
                    box. A clickable box cannot be tabbed to and is read out as plain
                    text, so anyone not using a mouse had no way to follow it. */}
                {alerts.map((alert) => (
                  <Alert
                    key={alert.text}
                    severity={alert.severity}
                    sx={{ py: 0 }}
                    action={
                      alert.report ? (
                        <Button
                          size="small"
                          color="inherit"
                          onClick={() => navigate(`${routes.reports}?report=${alert.report}`)}
                        >
                          See which
                        </Button>
                      ) : undefined
                    }
                  >
                    <b>{alert.count}</b> {alert.text}
                  </Alert>
                ))}
              </Stack>
            )}
          </Card>
        </Grid>

        {/* ----------------------------------------------- top selling */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary.main">
              TOP SELLING &middot; {periodLabel[period].toUpperCase()}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {rangeHint}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {bestSellers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nothing sold in this period.
              </Typography>
            ) : (
              bestSellers.map((product, index) => (
                <Stack key={product.productId} direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: '60%' }}>
                    {index + 1}. {product.productName}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {Number(product.quantity)} · ₹ {money(product.salesValue)}
                  </Typography>
                </Stack>
              ))
            )}
          </Card>
        </Grid>

        {/* -------------------------------------------- money in and out */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} color="success.main">
              MONEY DUE TO US &middot; RECEIVABLE
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2">
                Credit customers
                <Typography component="span" variant="caption" color="text.secondary">
                  {' '}
                  ({dueToUs.creditCustomers})
                </Typography>
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                ₹ {money(dueToUs.creditAmount)}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2">
                EMI banks
                <Typography component="span" variant="caption" color="text.secondary">
                  {' '}
                  ({dueToUs.emiBills})
                </Typography>
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                ₹ {money(dueToUs.emiAmount)}
              </Typography>
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} color="success.main">
                ₹ {money(dueToUs.total)}
              </Typography>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={700} color="error.main">
              MONEY WE OWE &middot; PAYABLE
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {weOwe.rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Every supplier invoice is settled.
              </Typography>
            ) : (
              weOwe.rows.map((supplier) => (
                <Stack key={supplier.supplierId} direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: '60%' }}>
                    {supplier.supplierName}
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}
                      ({supplier.invoiceCount})
                    </Typography>
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹ {money(supplier.dueAmount)}
                  </Typography>
                </Stack>
              ))
            )}
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="subtitle2" fontWeight={700}>
                Total
              </Typography>
              <Typography variant="subtitle2" fontWeight={700} color="error.main">
                ₹ {money(weOwe.total)}
              </Typography>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
